import { useState, useCallback, useRef, useEffect } from 'react';
import type { PlaybackState, ResolvedTrack, PreviewTrackResolutionResult } from '../types';

interface PreviewPlayerState {
  playbackState: PlaybackState;
  isPlaying: boolean;
  currentTrackIndex: number;
  currentArtworkUrl: string | null;
  previewTracksPlayed: number;
}

interface UsePreviewPlayerReturn extends PreviewPlayerState {
  setQueue: (tracks: ResolvedTrack[]) => void;
  play: () => Promise<void>;
  pause: () => void;
  skipToNext: () => Promise<void>;
  skipToPrevious: () => Promise<void>;
  skipToTrack: (index: number) => Promise<void>;
  resolveIsrcsPreview: (isrcs: string[]) => Promise<PreviewTrackResolutionResult>;
  reset: () => void;
}

export function usePreviewPlayer(): UsePreviewPlayerReturn {
  const [state, setState] = useState<PreviewPlayerState>({
    playbackState: 'none',
    isPlaying: false,
    currentTrackIndex: 0,
    currentArtworkUrl: null,
    previewTracksPlayed: 0,
  });

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const tracksRef = useRef<ResolvedTrack[]>([]);
  const currentTrackIndexRef = useRef(0);

  // Initialize audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }

    const audio = audioRef.current;

    const handleEnded = () => {
      // Auto-advance to next track
      const nextIndex = currentTrackIndexRef.current + 1;
      if (nextIndex < tracksRef.current.length) {
        skipToTrackInternal(nextIndex);
      } else {
        // Playlist ended
        setState(prev => ({
          ...prev,
          playbackState: 'stopped',
          isPlaying: false,
        }));
      }
    };

    const handlePlaying = () => {
      setState(prev => ({
        ...prev,
        playbackState: 'playing',
        isPlaying: true,
      }));
    };

    const handlePause = () => {
      setState(prev => ({
        ...prev,
        playbackState: 'paused',
        isPlaying: false,
      }));
    };

    const handleWaiting = () => {
      setState(prev => ({
        ...prev,
        playbackState: 'loading',
      }));
    };

    const handleError = () => {
      console.warn('Preview playback error, skipping to next track');
      // On error, skip to next track
      const nextIndex = currentTrackIndexRef.current + 1;
      if (nextIndex < tracksRef.current.length) {
        skipToTrackInternal(nextIndex);
      }
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('error', handleError);
    };
    // Note: We intentionally don't include state.currentTrackIndex in deps
    // to avoid re-adding listeners. The handlers use the ref value instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    currentTrackIndexRef.current = state.currentTrackIndex;
  }, [state.currentTrackIndex]);

  // Internal function to skip to a specific track
  const skipToTrackInternal = useCallback(async (index: number) => {
    const tracks = tracksRef.current;
    if (index < 0 || index >= tracks.length) return;

    // Find next track with a preview URL
    let targetIndex = index;
    while (targetIndex < tracks.length && !tracks[targetIndex].previewUrl) {
      targetIndex++;
    }

    if (targetIndex >= tracks.length) {
      // No more tracks with previews
      setState(prev => ({
        ...prev,
        playbackState: 'stopped',
        isPlaying: false,
      }));
      return;
    }

    const track = tracks[targetIndex];
    const audio = audioRef.current;
    if (!audio || !track.previewUrl) return;

    currentTrackIndexRef.current = targetIndex;
    setState(prev => ({
      ...prev,
      playbackState: 'loading',
      currentTrackIndex: targetIndex,
      currentArtworkUrl: track.artworkUrl ?? null,
      previewTracksPlayed: prev.previewTracksPlayed + 1,
    }));

    audio.src = track.previewUrl;
    try {
      await audio.play();
    } catch (error) {
      console.warn('Failed to play preview:', error);
      // Browser might block autoplay, update state to allow manual play
      setState(prev => ({
        ...prev,
        playbackState: 'paused',
        isPlaying: false,
      }));
    }
  }, []);

  const setQueue = useCallback((tracks: ResolvedTrack[]) => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.src = '';
    }
    tracksRef.current = tracks;
    currentTrackIndexRef.current = 0;
    setState(prev => ({
      ...prev,
      currentTrackIndex: 0,
      playbackState: 'none',
      isPlaying: false,
      currentArtworkUrl: tracks[0]?.artworkUrl ?? null,
    }));
  }, []);

  const play = useCallback(async () => {
    const audio = audioRef.current;
    const tracks = tracksRef.current;

    if (!audio || tracks.length === 0) return;

    // If no source set yet, start from current track
    if (!audio.src || audio.src === '') {
      await skipToTrackInternal(state.currentTrackIndex);
      return;
    }

    try {
      await audio.play();
    } catch (error) {
      console.warn('Failed to play:', error);
    }
  }, [state.currentTrackIndex, skipToTrackInternal]);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
    }
  }, []);

  const skipToNext = useCallback(async () => {
    await skipToTrackInternal(state.currentTrackIndex + 1);
  }, [state.currentTrackIndex, skipToTrackInternal]);

  const skipToPrevious = useCallback(async () => {
    await skipToTrackInternal(Math.max(0, state.currentTrackIndex - 1));
  }, [state.currentTrackIndex, skipToTrackInternal]);

  const skipToTrack = useCallback(async (index: number) => {
    await skipToTrackInternal(index);
  }, [skipToTrackInternal]);

  // Resolve ISRCs to preview URLs (no auth required)
  const resolveIsrcsPreview = useCallback(async (isrcs: string[]): Promise<PreviewTrackResolutionResult> => {
    const response = await fetch('/api/resolve-tracks-preview', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        isrcs,
        storefront: 'us', // Default to US storefront for preview mode
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to resolve preview tracks');
    }

    return response.json();
  }, []);

  const reset = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.src = '';
    }
    tracksRef.current = [];
    currentTrackIndexRef.current = 0;
    setState({
      playbackState: 'none',
      isPlaying: false,
      currentTrackIndex: 0,
      currentArtworkUrl: null,
      previewTracksPlayed: 0,
    });
  }, []);

  return {
    ...state,
    setQueue,
    play,
    pause,
    skipToNext,
    skipToPrevious,
    skipToTrack,
    resolveIsrcsPreview,
    reset,
  };
}
